import { $axiosMcp } from '@/api/$axios';

// `GET /ai-actions/{id}` is not a free read: for a pending gated row it runs
// the backend's `adapters.prepare()`, a live upstream call that snapshots the
// review baseline and fails the row outright if the entity changed since it
// was proposed. The ETag is weak `W/"{lifecycle_version}-{hash}"` and echoed exactly, so a
// conditional re-read costs a 304 and skips that work entirely. Every repeat
// read of a row we already hold must go through `etags`.
const etags = new Map();

const appConfig = () => ({
  baseURL: import.meta.env.VITE_API_MCP_API_URL?.replace(/\/api\/v1\/?$/, '/app/v1'),
});

export const appApi = {
  async list(params = {}, signal) {
    const { queue_tab, limit, cursor, agent_name, client_name, client_type, session_ref } = params;
    const response = await $axiosMcp.get('/ai-actions', {
      ...appConfig(),
      params: { tab: queue_tab, limit, cursor, agent_name, client_name, client_type, session_ref },
      signal,
    });
    return response.data;
  },
  async history({ limit, cursor = null, historyOffset = 0, includeQueue = true, includeLegacy = true, q = '', identity = {} } = {}, signal) {
    const [response, legacyResponse] = await Promise.all([
      includeQueue ? $axiosMcp.get('/ai-actions/history', {
        ...appConfig(),
        params: {
          limit,
          ...(cursor ? { cursor } : {}),
          ...(q ? { q } : {}),
          ...identity,
        },
        signal,
      }) : null,
      includeLegacy ? $axiosMcp.get('/ai-actions/history/legacy', {
        ...appConfig(),
        params: { offset: historyOffset || 0, limit, ...(q ? { q } : {}) },
        signal,
      }).catch(() => ({ data: { error: 'legacy_history_unavailable' } })) : null,
    ]);
    const data = response?.data;
    const legacy = legacyResponse?.data;
    return {
      items: data?.items || [],
      next_cursor: data?.next_cursor ?? null,
      legacyItems: legacy?.items || [],
      legacyNextOffset: legacy?.next_offset ?? null,
      legacyError: legacy?.error || '',
      searchTruncated: includeQueue ? data?.search_truncated === true : null,
      searchError: includeQueue ? data?.search_error || '' : null,
      searchIgnored: false,
    };
  },
  async get(id, signal, cached = null) {
    const etag = cached ? etags.get(id) : undefined;
    const response = await $axiosMcp.get(`/ai-actions/${encodeURIComponent(id)}`, {
      ...appConfig(),
      signal,
      headers: etag ? { 'If-None-Match': etag } : undefined,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    });
    if (response.status === 304) return null;
    const fresh = response.headers?.etag;
    if (fresh) etags.set(id, fresh);
    else etags.delete(id);
    return response.data;
  },
  async review(items, decision, reason = '') {
    const response = await $axiosMcp.post('/ai-actions/review', { items, decision, reason }, appConfig());
    items.forEach((item) => etags.delete(item.id));
    return response.data;
  },
  async cancel(id, expectedVersion, reason = '') {
    const response = await $axiosMcp.post(`/ai-actions/${encodeURIComponent(id)}/cancel`, {
      expected_version: expectedVersion,
      reason,
    }, appConfig());
    etags.delete(id);
    return response.data;
  },
  async resolve(id, expectedVersion, outcome, note) {
    const response = await $axiosMcp.post(`/ai-actions/${encodeURIComponent(id)}/resolve`, {
      expected_version: expectedVersion,
      outcome,
      note,
    }, appConfig());
    etags.delete(id);
    return response.data;
  },
  async comment(id, body) {
    const response = await $axiosMcp.post(`/ai-actions/${encodeURIComponent(id)}/comment`, { body }, appConfig());
    const fresh = response.headers?.etag;
    if (fresh) etags.set(id, fresh);
    else etags.delete(id);
    return response.data;
  },
  async dismiss(id, reason = '') {
    const response = await $axiosMcp.post(`/ai-actions/${encodeURIComponent(id)}/dismiss`, reason ? { reason } : {}, appConfig());
    etags.delete(id);
    return response.data;
  },
  async restore(id) {
    const response = await $axiosMcp.post(`/ai-actions/${encodeURIComponent(id)}/restore`, {}, appConfig());
    etags.delete(id);
    return response.data;
  },
  async types(signal) {
    const response = await $axiosMcp.get('/ai-actions/types', { ...appConfig(), signal });
    return response.data;
  },
  async badge() {
    const response = await $axiosMcp.get('/ai-actions/badge', appConfig());
    return response.data;
  },
  forget(id) {
    etags.delete(id);
  },
};

export const mutationsApi = appApi;
